import type { FoodSearchResult, NutritionRowEstimate, ParsedVoiceMealDraft } from "./remote-meal-parser";

export interface UserFood {
  id: string;
  name: string;
  aliases: string[];
  grams: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  servingGrams?: number;
  components?: Array<{ name: string; grams: number }>;
  updatedAt: string;
}

const STORAGE_KEY = "yumnote.userFoods.v1";
const normalize = (value: string) => value.toLocaleLowerCase("ru-RU").replace(/ё/gu, "е").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
const gramsFor = (row: NutritionRowEstimate) => row.components.reduce((sum, component) => sum + (component.grams ?? 0), 0);

export function loadUserFoods(): UserFood[] {
  if (typeof localStorage === "undefined") return [];
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as UserFood[]; } catch { return []; }
}

export function findUserFood(name: string): UserFood | null {
  const key = normalize(name);
  return loadUserFoods().find(food => [food.name, ...food.aliases].some(alias => normalize(alias) === key)) ?? null;
}

export function saveUserFood(input: Omit<UserFood, "id" | "updatedAt" | "aliases"> & { aliases?: string[] }): UserFood {
  const foods = loadUserFoods();
  const existingIndex = foods.findIndex(food => [food.name, ...food.aliases].some(alias => normalize(alias) === normalize(input.name)));
  const saved: UserFood = {
    ...input,
    id: existingIndex >= 0 ? foods[existingIndex]!.id : crypto.randomUUID(),
    aliases: [...new Set([input.name, ...(input.aliases ?? [])].map(value => value.trim()).filter(Boolean))],
    updatedAt: new Date().toISOString(),
  };
  if (existingIndex >= 0) foods[existingIndex] = saved; else foods.unshift(saved);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(foods));
  return saved;
}

export function applyUserFoods(draft: ParsedVoiceMealDraft, previous?: ParsedVoiceMealDraft): ParsedVoiceMealDraft {
  if (!draft.nutrition) return draft;
  const rows = draft.nutrition.rows.map((row, index) => {
    if (previous?.nutrition?.rows.some((saved, oldIndex) => JSON.stringify(saved) === JSON.stringify(row)
      && JSON.stringify(previous.items[oldIndex]) === JSON.stringify(draft.items[index]))) return row;
    if (row.profileKey || row.kcal100 !== undefined || row.kcalPortion !== undefined || row.components.length !== 1) return row;
    const food = findUserFood(row.name);
    const grams = gramsFor(row);
    if (!food || grams <= 0) return row;
    const factor = grams / 100;
    const values = { calories: food.calories * factor, protein: food.protein * factor, fat: food.fat * factor, carbs: food.carbs * factor };
    return { ...row, ...values, components: [{ ...row.components[0]!, name: food.name, ...values }], valueSource: "stated" as const, isUserFood: true };
  });
  const total = (field: "calories" | "protein" | "fat" | "carbs") => Math.round(rows.reduce((sum, row) => sum + (row[field] ?? 0), 0) * 10) / 10;
  return { ...draft, nutrition: { rows, totalCalories: total("calories"), totalProtein: total("protein"), totalFat: total("fat"), totalCarbs: total("carbs") } };
}

export function userFoodSearchResults(query: string): FoodSearchResult[] {
  const key = normalize(query);
  return loadUserFoods().filter(food => [food.name, ...food.aliases].some(alias => normalize(alias).includes(key) || key.includes(normalize(alias)))).map((food, index) => ({
    id: -(index + 1), name: food.name, matchedName: food.name, isUserFood: true,
    nutrition: { foodId: -(index + 1), name: food.name, grams: 100, calories: food.calories, protein: food.protein, fat: food.fat, carbs: food.carbs, unit: "g", source: "user" },
  }));
}
